/**
 * The lines every loading screen types out (the user, 2026-10-07) — kept in
 * the database (`platform.loading_line`, `GET /loading-lines`) and on the
 * device (`myrave:platform:loading-lines`), so a loading screen has them with
 * no signal and before anything has answered. Asked again once per page load,
 * never waited on.
 *
 * `{rave}` lines name the rave that is opening ("Your OZORA experience is
 * waiting in heaven…"). The name is the one the launch screen remembers for
 * THIS origin (`splash.ts`), so they appear only inside a rave, and only
 * after it has been opened once.
 *
 * In every language (2026-10-27): the API answers each line in the language
 * asked for (`local`, in `lang`), so the list is asked for in the page's
 * language — again whenever that changes. A language a line has no words in
 * leaves it out; a language with none at all shows the English.
 *
 * The launch screen in index.html reads the same stored list by its literal
 * key (`loadingLines.test.ts` holds them together) — `local` first.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '@/lib/api';
import { readJson, writeJson } from '@/lib/storage';
import { LoadingLinesProvider, type LoadingLinePool } from '@/ui';
import { languageOf } from '@myrave/shared';
import { signedInHere } from '@/lib/signOut';
import { useLocale } from './LocaleProvider';
import { SPLASH_NAME, type SplashRave } from './splash';
import { resolveTenant } from '@/lib/tenant';
import { storedTenant } from '@/offline/tenantCache';

export const LINES_NAME = 'loading-lines';

/**
 * `text` the English, `he` the Hebrew column; `local` the line in `lang`
 * (null where that language has none). A list stored before 2026-10-27 has
 * neither `local` nor `lang`.
 */
export interface StoredLine { text: string; he: string | null; local?: string | null; lang?: string }

const asked = new Map<string, Promise<StoredLine[] | null>>();

/**
 * The fresh list in a locale, once per page load and language; null when it
 * could not be had. The locale goes in the request's own header: the page's
 * `<html lang>` may not be set yet when the first screen asks.
 */
export function refreshLoadingLines(locale: string): Promise<StoredLine[] | null> {
  const language = languageOf(locale);
  let pending = asked.get(language);
  if (!pending) {
    pending = api.get<{ lines: StoredLine[] }>('/loading-lines', { 'x-myrave-locale': locale })
      .then(({ lines }) => {
        if (!Array.isArray(lines) || lines.length === 0) return null;
        writeJson(null, LINES_NAME, lines);
        return lines;
      })
      .catch(() => null);
    asked.set(language, pending);
  }
  return pending;
}

/** One line in a language, or null when it has none there. */
function lineIn(l: StoredLine, language: string): string | null {
  if (language === 'en') return l.text;
  if (l.lang === language && l.local) return l.local;
  return language === 'he' ? l.he : null;
}

/** The pool for a locale and a rave name — exported for the test. */
export function poolFrom(lines: readonly StoredLine[], locale: string, rave: string | null, name: string | null = null): LoadingLinePool {
  const language = languageOf(locale);
  const pick = (lang: string) => lines
    .map((l) => lineIn(l, lang))
    .filter((t): t is string => typeof t === 'string' && t.length > 0);
  const own = pick(language);
  // Nothing in this language (a list stored in another, or none written): English.
  const said = own.some((t) => !t.includes('{rave}') && !t.includes('{name}')) ? own : pick('en');
  return {
    general: said.filter((t) => !t.includes('{rave}') && !t.includes('{name}')),
    rave: rave ? said.filter((t) => t.includes('{rave}') && !t.includes('{name}')).map((t) => t.split('{rave}').join(rave)) : [],
    // Step 98: the person's first name — never a line with `{name}` left in it.
    ...(name ? { name: said.filter((t) => t.includes('{name}') && !t.includes('{rave}')).map((t) => t.split('{name}').join(name)), who: name } : {}),
  };
}

export function LoadingLines({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const [lines, setLines] = useState<StoredLine[]>(() => readJson<StoredLine[]>(null, LINES_NAME, []));
  // The rave's name: what the launch screen remembered, else the stored
  // `/tenant` answer for this subdomain — so the rave's lines are there on the
  // very first visit, not only once the rave has opened here before.
  const sub = resolveTenant().subdomain;
  const rave = readJson<SplashRave | null>(null, SPLASH_NAME, null)?.festival
    ?? (sub ? storedTenant(sub)?.tenant?.festivalName : null)
    ?? null;

  useEffect(() => {
    // Not under the tests: their fetch is a recording, and a request nobody
    // asked for would be one more thing for every one of them to answer.
    if (import.meta.env.MODE === 'test') return;
    let live = true;
    void refreshLoadingLines(locale).then((fresh) => { if (live && fresh) setLines(fresh); });
    return () => { live = false; };
  }, [locale]);

  // The signed-in person's first name (step 98), from what this device remembers — offline too; nobody signed out.
  const name = rememberedFirstName(locale);
  const pool = useMemo(() => poolFrom(lines, locale, rave, name), [lines, locale, rave, name]);
  // Nothing stored yet (a first visit offline): the screens' built-in lines.
  return pool.general.length > 0
    ? <LoadingLinesProvider value={pool}>{children}</LoadingLinesProvider>
    : <>{children}</>;
}

/**
 * The first name a loading line greets (step 98): the account this device remembers (`session-user`), only while
 * it is signed in here, its name in the reader's language when one was kept (`nameLocal`), else as written. The
 * first word only — never an email, never a username.
 */
export function rememberedFirstName(locale: string): string | null {
  const me = readJson<{ id?: string; displayName?: string | null; nameLocal?: Record<string, string> } | null>(null, 'session-user', null);
  if (!me?.id || signedInHere() !== me.id) return null;
  const local = me.nameLocal?.[languageOf(locale)];
  return firstNameOf(local ?? me.displayName ?? null);
}

export function firstNameOf(name: string | null): string | null {
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return first && !first.includes('@') && first.length <= 30 ? first : null;
}
